import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Message,
  MessageThread,
  MessageInput,
  BroadcastInput,
  NotificationPreference,
} from '@pirouette/shared';
import { messageInputSchema } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';

// Threads ------------------------------------------------------------------

export function useThreads() {
  return useQuery({
    queryKey: ['threads'],
    queryFn: async (): Promise<MessageThread[]> => {
      const { data, error } = await supabase
        .from('message_threads')
        .select('*')
        .eq('archived', false)
        .order('last_message_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useThread(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['thread', id],
    queryFn: async (): Promise<MessageThread | null> => {
      const { data, error } = await supabase
        .from('message_threads')
        .select('*')
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

export function useCreateThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      family_id: string;
      subject: string;
      firstMessage: string;
    }): Promise<MessageThread> => {
      const userResp = await supabase.auth.getUser();
      const userId = userResp.data.user?.id;
      const role =
        (userResp.data.user?.app_metadata.role as 'admin' | 'faculty' | 'parent' | 'dancer') ??
        'parent';
      if (!userId) throw new Error('Not authenticated');

      // Two-step: create thread, then first message. Both are RLS-protected;
      // no service-role bypass needed.
      const { data: thread, error: e1 } = await supabase
        .from('message_threads')
        .insert({
          family_id: args.family_id,
          subject: args.subject.trim(),
        })
        .select('*')
        .single();
      if (e1) throw e1;

      const { error: e2 } = await supabase.from('messages').insert({
        thread_id: thread.id,
        author_user_id: userId,
        author_role: role,
        body: args.firstMessage.trim(),
      });
      if (e2) throw e2;

      return thread;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

// Messages -----------------------------------------------------------------

export function useMessages(threadId: string | undefined) {
  return useQuery({
    enabled: !!threadId,
    queryKey: ['messages', threadId],
    queryFn: async (): Promise<Message[]> => {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('thread_id', threadId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MessageInput): Promise<Message> => {
      const parsed = messageInputSchema.parse(input);
      const userResp = await supabase.auth.getUser();
      const userId = userResp.data.user?.id;
      const role =
        (userResp.data.user?.app_metadata.role as 'admin' | 'faculty' | 'parent' | 'dancer') ??
        'parent';
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('messages')
        .insert({
          thread_id: parsed.thread_id,
          author_user_id: userId,
          author_role: role,
          body: parsed.body,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (msg) => {
      void qc.invalidateQueries({ queryKey: ['messages', msg.thread_id] });
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

export function useMarkMessageRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from('messages')
        .update({ read_by_recipient_at: new Date().toISOString() })
        .eq('id', messageId);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

// Broadcasts ---------------------------------------------------------------

export interface BroadcastRow {
  id: string;
  created_at: string;
  scope: 'all' | 'class' | 'comp';
  scope_target_id: string | null;
  title: string;
  body: string;
  channels: string[];
  scheduled_for: string | null;
  sent_at: string | null;
}

export function useBroadcasts() {
  return useQuery({
    queryKey: ['broadcasts'],
    queryFn: async (): Promise<BroadcastRow[]> => {
      const { data, error } = await supabase
        .from('broadcast_announcements')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateBroadcast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BroadcastInput): Promise<BroadcastRow> => {
      const userResp = await supabase.auth.getUser();
      const userId = userResp.data.user?.id;
      if (!userId) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('broadcast_announcements')
        .insert({
          created_by: userId,
          scope: input.scope,
          scope_target_id: input.scope_target_id ?? null,
          title: input.title,
          body: input.body,
          channels: input.channels,
          scheduled_for: input.scheduled_for ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['broadcasts'] });
    },
  });
}

// Preferences --------------------------------------------------------------

export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async (): Promise<NotificationPreference[]> => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('user_id, kind, channel, enabled');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSetNotificationPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      kind: NotificationPreference['kind'];
      channel: NotificationPreference['channel'];
      enabled: boolean;
    }) => {
      const userResp = await supabase.auth.getUser();
      const userId = userResp.data.user?.id;
      if (!userId) throw new Error('Not authenticated');
      const { error } = await supabase.from('notification_preferences').upsert(
        {
          user_id: userId,
          kind: input.kind,
          channel: input.channel,
          enabled: input.enabled,
        },
        { onConflict: 'user_id,kind,channel' },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });
}
